import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { PageHeader, Btn, Input, Card, useToast } from '../components/shared/UI';
import PasswordStrength, { checkPassword } from '../components/shared/PasswordStrength';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// Best-effort browser + OS label from a user-agent string
function describeDevice(uaArg) {
  const ua = uaArg != null ? uaArg : (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!ua) return '—';
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : '';
  return os ? `${browser} · ${os}` : browser;
}

// ── Two-factor authentication ─────────────────────────────────
function TwoFactorSection({ t, user, updateUser, show }) {
  const method = user?.twoFactorMethod || 'off';
  const [flow, setFlow] = useState(null);   // null | 'totp' | 'email' | 'disable'
  const [setup, setSetup] = useState(null); // { secret, otpauth } for totp
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (setup?.otpauth) QRCode.toDataURL(setup.otpauth, { width: 180, margin: 1 }).then(setQr).catch(() => setQr(null));
    else setQr(null);
  }, [setup?.otpauth]);

  const reset = () => { setFlow(null); setSetup(null); setCode(''); setPassword(''); setErr(''); };

  const startTotp = async () => {
    setErr(''); setBusy(true);
    try {
      const { data } = await api.post('/auth/2fa/totp/setup');
      setSetup(data); setFlow('totp'); setCode('');
    } catch (e) { show(e.response?.data?.message || t('profile.updateFailed'), 'error'); }
    finally { setBusy(false); }
  };

  const startEmail = async () => {
    setErr(''); setBusy(true);
    try {
      await api.post('/auth/2fa/email/setup');
      setFlow('email'); setCode('');
    } catch (e) { show(e.response?.data?.message || t('profile.updateFailed'), 'error'); }
    finally { setBusy(false); }
  };

  const confirm = async e => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const url = flow === 'totp' ? '/auth/2fa/totp/enable' : '/auth/2fa/email/enable';
      const { data } = await api.post(url, { code: code.trim() });
      updateUser({ twoFactorMethod: data.twoFactorMethod });
      show(t('twofa.enabled'));
      reset();
    } catch (e2) { setErr(e2.response?.data?.message || t('profile.updateFailed')); }
    finally { setBusy(false); }
  };

  const disable = async e => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const { data } = await api.post('/auth/2fa/disable', { password });
      updateUser({ twoFactorMethod: data.twoFactorMethod });
      show(t('twofa.disabled'));
      reset();
    } catch (e2) { setErr(e2.response?.data?.message || t('profile.updateFailed')); }
    finally { setBusy(false); }
  };

  const codeInput = (
    <input value={code} onChange={e => setCode(e.target.value.replace(/[^\d\s]/g, ''))}
      inputMode="numeric" autoComplete="one-time-code" placeholder="000000" dir="ltr" required
      style={{
        width: 160, textAlign: 'center', fontSize: 20, letterSpacing: 8, fontWeight: 700,
        fontFamily: "'Courier New', monospace", padding: '10px 6px',
        border: '1.5px solid var(--border)', borderRadius: 8,
        background: 'var(--input-bg)', color: 'var(--text)',
      }} />
  );

  return (
    <Section title={t('twofa.section')} subtitle={t('twofa.sectionSub')}>
      {/* Status row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{
          fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
          background: method === 'off' ? 'rgba(148,163,184,0.15)' : 'rgba(16,185,129,0.15)',
          color: method === 'off' ? 'var(--text-muted)' : 'var(--success)',
        }}>
          {method === 'off' ? t('twofa.statusOff') : method === 'totp' ? t('twofa.statusApp') : t('twofa.statusEmail')}
        </span>
      </div>

      {method === 'off' && !flow && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Btn variant="primary" onClick={startTotp} disabled={busy}>📱 {t('twofa.useApp')}</Btn>
          <Btn variant="ghost" onClick={startEmail} disabled={busy || !user?.emailVerified}
            title={user?.emailVerified ? '' : t('banner.verifyEmail')}>✉️ {t('twofa.useEmail')}</Btn>
        </div>
      )}

      {flow === 'totp' && setup && (
        <form onSubmit={confirm}>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12 }}>{t('twofa.scanQr')}</p>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 14 }}>
            {qr && <img src={qr} alt="QR" style={{ width: 150, height: 150, borderRadius: 10, border: '1px solid var(--border)', background: '#fff', padding: 6 }} />}
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginBottom: 6 }}>{t('twofa.manualKey')}</p>
              <code dir="ltr" style={{
                display: 'block', fontSize: 13, fontWeight: 700, letterSpacing: 1, wordBreak: 'break-all',
                background: 'var(--hover-bg)', border: '1px solid var(--border)', borderRadius: 8,
                padding: '10px 12px', color: 'var(--heading)', fontFamily: "'Courier New', monospace",
              }}>{setup.secret}</code>
              <p style={{ fontSize: 12, color: 'var(--text-subtle)', margin: '14px 0 6px' }}>{t('twofa.thenEnter')}</p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {codeInput}
                <Btn type="submit" variant="primary" disabled={busy || code.trim().length < 6}>{t('twofa.activate')}</Btn>
                <Btn variant="ghost" onClick={reset}>{t('common.cancel')}</Btn>
              </div>
            </div>
          </div>
          {err && <p style={{ fontSize: 13, color: 'var(--danger)' }}>{err}</p>}
        </form>
      )}

      {flow === 'email' && (
        <form onSubmit={confirm}>
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
            {t('twofa.emailSent', { email: user?.email })}
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {codeInput}
            <Btn type="submit" variant="primary" disabled={busy || code.trim().length < 6}>{t('twofa.activate')}</Btn>
            <Btn variant="ghost" onClick={reset}>{t('common.cancel')}</Btn>
          </div>
          {err && <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 10 }}>{err}</p>}
        </form>
      )}

      {method !== 'off' && flow !== 'disable' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.6 }}>
            {method === 'totp' ? t('twofa.activeApp') : t('twofa.activeEmail')}
          </p>
          <Btn variant="danger" onClick={() => { setFlow('disable'); setErr(''); }}>{t('twofa.turnOff')}</Btn>
        </div>
      )}

      {flow === 'disable' && (
        <form onSubmit={disable} style={{ marginTop: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>{t('twofa.confirmPassword')}</p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" required autoComplete="current-password" style={{ width: 200 }} />
            <Btn type="submit" variant="danger" disabled={busy || !password}>{t('twofa.turnOff')}</Btn>
            <Btn variant="ghost" onClick={reset}>{t('common.cancel')}</Btn>
          </div>
          {err && <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 10 }}>{err}</p>}
        </form>
      )}
    </Section>
  );
}

// Small on/off switch
function Switch({ checked, onChange, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={onChange} disabled={disabled}
      style={{
        width: 42, height: 24, borderRadius: 12, border: 'none', cursor: disabled ? 'default' : 'pointer',
        background: checked ? 'var(--success)' : 'var(--border)', position: 'relative', flexShrink: 0,
        transition: 'background 0.15s', opacity: disabled ? 0.6 : 1, padding: 0,
      }}>
      <span style={{
        position: 'absolute', top: 2, insetInlineStart: checked ? 20 : 2, width: 20, height: 20,
        borderRadius: '50%', background: '#fff', transition: 'inset-inline-start 0.15s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </button>
  );
}

// ── Email notification preferences ─────────────────────────────
function EmailPrefsSection({ t, user, updateUser, show }) {
  const prefs = user?.emailPrefs || { announcements: true, securityAlerts: true };
  const [saving, setSaving] = useState('');

  const toggle = async key => {
    setSaving(key);
    try {
      const { data } = await api.put('/auth/email-prefs', { [key]: !prefs[key] });
      updateUser({ emailPrefs: data.emailPrefs });
    } catch (err) {
      show(err.response?.data?.message || t('profile.updateFailed'), 'error');
    } finally { setSaving(''); }
  };

  const rows = [
    { key: 'securityAlerts', label: t('emailPrefs.securityAlerts'), desc: t('emailPrefs.securityAlertsDesc') },
    { key: 'announcements', label: t('emailPrefs.announcements'), desc: t('emailPrefs.announcementsDesc') },
  ];

  return (
    <Section title={t('emailPrefs.title')} subtitle={t('emailPrefs.subtitle')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {rows.map(r => (
          <div key={r.key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{r.label}</p>
              <p style={{ fontSize: 12.5, color: 'var(--text-subtle)', marginTop: 2, lineHeight: 1.5 }}>{r.desc}</p>
            </div>
            <Switch checked={prefs[r.key] !== false} onChange={() => toggle(r.key)} disabled={saving === r.key} />
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 14, lineHeight: 1.5, paddingTop: 12, borderTop: '1px solid var(--border-light)' }}>
        {t('emailPrefs.transactionalNote')}
      </p>
    </Section>
  );
}

// ── Active sessions ────────────────────────────────────────────
function SessionsSection({ t, user, logout }) {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () => api.get('/auth/sessions').then(r => setSessions(r.data)).catch(() => setSessions([]));
  useEffect(() => { load(); }, []);

  const relTime = d => {
    const diff = Date.now() - new Date(d).getTime();
    const min = Math.round(diff / 60000);
    if (min < 1) return t('sessions.now');
    if (min < 60) return t('sessions.minAgo', { n: min });
    const hr = Math.round(min / 60);
    if (hr < 24) return t('sessions.hrAgo', { n: hr });
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const revoke = async s => {
    setBusy(s.id);
    try {
      const { data } = await api.post(`/auth/sessions/${s.id}/revoke`);
      if (data.current) { logout(); return; } // revoked our own session
      load();
    } catch { /* ignore */ } finally { setBusy(''); }
  };

  const revokeOthers = async () => {
    setBusy('others');
    try { await api.post('/auth/sessions/revoke-others'); load(); }
    catch { /* ignore */ } finally { setBusy(''); }
  };

  const others = (sessions || []).filter(s => !s.current).length;

  return (
    <Section title={t('sessions.title')} subtitle={t('sessions.subtitle')}>
      {/* Account summary */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {[
          { label: t('profile.signedInAs'), value: `${user?.name || ''}${user?.email ? ` · ${user.email}` : ''}` },
          { label: t('profile.memberSince'), value: user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—' },
          { label: t('twofa.section'), value: (user?.twoFactorMethod && user.twoFactorMethod !== 'off') ? t('profile.protected') : t('twofa.statusOff') },
        ].map(r => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-subtle)', flexShrink: 0 }}>{r.label}</span>
            <span style={{ color: 'var(--text)', fontWeight: 500, textAlign: 'end', wordBreak: 'break-word' }}>{r.value}</span>
          </div>
        ))}
      </div>

      {/* Live device list */}
      <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 14 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--heading)', marginBottom: 10 }}>{t('sessions.activeDevices')}</p>
        {sessions == null ? (
          <p style={{ fontSize: 13, color: 'var(--text-subtle)' }}>{t('common.loading')}</p>
        ) : sessions.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-subtle)' }}>{t('sessions.none')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {sessions.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                background: s.current ? 'rgba(16,185,129,0.08)' : 'var(--hover-bg)',
                border: `1px solid ${s.current ? 'rgba(16,185,129,0.35)' : 'var(--border)'}`,
                borderRadius: 8, padding: '10px 12px',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    {describeDevice(s.userAgent)}
                    {s.current && <span style={{ marginInlineStart: 8, fontSize: 10, fontWeight: 700, color: 'var(--success)', background: 'rgba(16,185,129,0.15)', borderRadius: 10, padding: '1px 7px' }}>{t('sessions.thisDevice')}</span>}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-subtle)', marginTop: 2 }}>
                    {s.ip ? `${s.ip} · ` : ''}{t('sessions.lastActive', { when: relTime(s.lastSeenAt) })}
                  </div>
                </div>
                {s.current ? (
                  <span style={{ fontSize: 12, color: 'var(--text-subtle)' }}>—</span>
                ) : (
                  <Btn size="sm" variant="outline" onClick={() => revoke(s)} disabled={busy === s.id}>
                    {busy === s.id ? '…' : t('sessions.revoke')}
                  </Btn>
                )}
              </div>
            ))}
          </div>
        )}
        {others > 0 && (
          <div style={{ marginTop: 12 }}>
            <Btn size="sm" variant="danger" onClick={revokeOthers} disabled={busy === 'others'}>
              {busy === 'others' ? '…' : t('sessions.signOutOthers')}
            </Btn>
          </div>
        )}
      </div>

      {/* Sign out (current) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderTop: '1px solid var(--border-light)', paddingTop: 14, marginTop: 14 }}>
        <div>
          <p style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{t('profile.signOutOf')}</p>
          <p style={{ fontSize: 13, color: 'var(--text-subtle)', marginTop: 2 }}>{t('profile.redirected')}</p>
        </div>
        <Btn variant="danger" onClick={logout}>{t('nav.signOut')}</Btn>
      </div>
    </Section>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <Card style={{ marginBottom: 20 }}>
      <div style={{ marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid var(--border-light)' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--heading)' }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--text-subtle)', marginTop: 3 }}>{subtitle}</p>}
      </div>
      {children}
    </Card>
  );
}

export default function Profile() {
  const { user, updateUser, logout } = useAuth();
  const { show, ToastContainer } = useToast();
  const { t } = useT();

  const [info, setInfo] = useState({ name: user?.name || '', email: user?.email || '' });
  const [emailPassword, setEmailPassword] = useState('');
  const [infoError, setInfoError] = useState('');
  const [pass, setPass] = useState({ current: '', newPass: '', confirm: '' });
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPass, setSavingPass] = useState(false);
  const [passError, setPassError] = useState('');

  const initials = (user?.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const emailChanged = (info.email || '').trim().toLowerCase() !== (user?.email || '').toLowerCase();

  const saveInfo = async e => {
    e.preventDefault();
    setInfoError('');
    // Changing the email needs the password and is confirmed at the new address
    if (emailChanged && !emailPassword) { setInfoError(t('profile.emailNeedsPassword')); return; }
    setSavingInfo(true);
    try {
      const payload = emailChanged
        ? { name: info.name, email: info.email, currentPassword: emailPassword }
        : { name: info.name };
      const { data } = await api.put('/auth/profile', payload);
      updateUser({ name: data.name, pendingEmail: data.pendingEmail || null });
      setEmailPassword('');
      if (data.pendingEmail) {
        show(t('profile.emailConfirmSent', { email: data.pendingEmail }));
        setInfo(f => ({ ...f, email: user.email })); // keep showing current until confirmed
      } else {
        show(t('profile.updated'));
      }
    } catch (err) {
      setInfoError(err.response?.data?.message || t('profile.updateFailed'));
    } finally { setSavingInfo(false); }
  };

  const [savingAvatar, setSavingAvatar] = useState(false);
  const avatarInputRef = React.useRef(null);

  const saveAvatar = async avatarUrl => {
    setSavingAvatar(true);
    try {
      const { data } = await api.put('/auth/avatar', { avatarUrl });
      updateUser({ avatarUrl: data.avatarUrl });
      show(avatarUrl ? t('profile.photoUpdated') : t('profile.photoRemoved'));
    } catch (err) {
      show(err.response?.data?.message || t('profile.updateFailed'), 'error');
    } finally { setSavingAvatar(false); }
  };

  const onAvatarPick = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 1024 * 1024) { show(t('profile.photoTooLarge'), 'error'); return; }
    const reader = new FileReader();
    reader.onloadend = () => saveAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const cancelEmailChange = async () => {
    try {
      const { data } = await api.post('/auth/email-change/cancel');
      updateUser({ pendingEmail: null });
      setInfo(f => ({ ...f, email: data.email }));
      show(t('profile.emailChangeCancelled'));
    } catch (err) {
      show(err.response?.data?.message || t('profile.updateFailed'), 'error');
    }
  };

  const savePassword = async e => {
    e.preventDefault();
    setPassError('');
    if (pass.newPass !== pass.confirm) { setPassError(t('profile.noMatch')); return; }
    if (!checkPassword(pass.newPass).valid) { setPassError(t('profile.weakPassword')); return; }
    setSavingPass(true);
    try {
      await api.put('/auth/profile', { currentPassword: pass.current, newPassword: pass.newPass });
      setPass({ current: '', newPass: '', confirm: '' });
      show(t('profile.changed'));
    } catch (err) {
      setPassError(err.response?.data?.message || t('profile.changeFailed'));
    } finally { setSavingPass(false); }
  };

  return (
    <div>
      <ToastContainer />
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 24, alignItems: 'start' }}>
        {/* Left */}
        <div>
          {/* Account info */}
          <form onSubmit={saveInfo}>
            <Section title={t('profile.accountInfo')} subtitle={t('profile.accountInfoSub')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Input label={t('profile.fullName')} value={info.name} onChange={e => setInfo(f => ({ ...f, name: e.target.value }))}
                  required placeholder={t('profile.fullNamePh')} />
                <Input label={t('profile.email')} type="email" value={info.email}
                  onChange={e => setInfo(f => ({ ...f, email: e.target.value }))}
                  required placeholder="you@example.com" />

                {/* Pending email change — awaiting confirmation at the new address */}
                {user?.pendingEmail && (
                  <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text)' }}>
                    <p style={{ marginBottom: 6 }}>⏳ {t('profile.emailPending', { email: user.pendingEmail })}</p>
                    <button type="button" onClick={cancelEmailChange}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', padding: 0 }}>
                      {t('profile.cancelEmailChange')}
                    </button>
                  </div>
                )}

                {/* Password required to change the login email */}
                {emailChanged && (
                  <div>
                    <Input label={t('profile.currentPassword')} type="password" value={emailPassword}
                      onChange={e => setEmailPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
                    <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 6, lineHeight: 1.5 }}>
                      🔒 {t('profile.emailChangeHint')}
                    </p>
                  </div>
                )}

                {infoError && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--danger)' }}>
                    {infoError}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <Btn type="submit" variant="primary" disabled={savingInfo}>
                  {savingInfo ? t('common.saving') : emailChanged ? t('profile.sendConfirmation') : t('editor.saveChanges')}
                </Btn>
              </div>
            </Section>
          </form>

          {/* Change password */}
          <form onSubmit={savePassword}>
            <Section title={t('profile.changePassword')} subtitle={t('profile.changePasswordSub')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Input label={t('profile.currentPassword')} type="password" value={pass.current}
                  onChange={e => setPass(f => ({ ...f, current: e.target.value }))}
                  required placeholder="••••••••" />
                <div>
                  <Input label={t('profile.newPassword')} type="password" value={pass.newPass}
                    onChange={e => setPass(f => ({ ...f, newPass: e.target.value }))}
                    required placeholder="••••••••" />
                  <PasswordStrength password={pass.newPass} />
                </div>
                <Input label={t('profile.confirmPassword')} type="password" value={pass.confirm}
                  onChange={e => setPass(f => ({ ...f, confirm: e.target.value }))}
                  required placeholder="••••••••" />
                {passError && (
                  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--danger)' }}>
                    {passError}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
                <Btn type="submit" variant="primary" disabled={savingPass}>
                  {savingPass ? t('profile.updating') : t('profile.updatePassword')}
                </Btn>
              </div>
            </Section>
          </form>

          {/* Two-factor authentication */}
          <TwoFactorSection t={t} user={user} updateUser={updateUser} show={show} />

          {/* Email notification preferences */}
          <EmailPrefsSection t={t} user={user} updateUser={updateUser} show={show} />

          {/* Active sessions & sign out */}
          <SessionsSection t={t} user={user} logout={logout} />
        </div>

        {/* Right — account card */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 24 }}>
          <Card>
            <input ref={avatarInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={onAvatarPick} style={{ display: 'none' }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '8px 0 16px' }}>
              <button type="button" onClick={() => avatarInputRef.current?.click()} disabled={savingAvatar}
                title={t('profile.changePhoto')}
                style={{
                  position: 'relative', width: 76, height: 76, borderRadius: '50%', marginBottom: 14,
                  padding: 0, border: 'none', cursor: savingAvatar ? 'default' : 'pointer', background: 'transparent',
                  opacity: savingAvatar ? 0.6 : 1,
                }}>
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" style={{ width: 76, height: 76, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <span style={{
                    width: 76, height: 76, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #1A3C6E, #2E75B6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 27, fontWeight: 700, color: '#fff', letterSpacing: 1,
                  }}>{initials}</span>
                )}
                <span style={{
                  position: 'absolute', bottom: 0, insetInlineEnd: 0, width: 26, height: 26, borderRadius: '50%',
                  background: 'var(--accent)', color: '#1A3C6E', border: '2px solid var(--card-bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
                }}>📷</span>
              </button>
              <p style={{ fontSize: 17, fontWeight: 700, color: 'var(--heading)' }}>{user?.name}</p>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{user?.email}</p>
              <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                <button type="button" onClick={() => avatarInputRef.current?.click()} disabled={savingAvatar}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', padding: 0 }}>
                  {user?.avatarUrl ? t('profile.changePhoto') : t('profile.uploadPhoto')}
                </button>
                {user?.avatarUrl && (
                  <button type="button" onClick={() => saveAvatar('')} disabled={savingAvatar}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', padding: 0 }}>
                    {t('profile.removePhoto')}
                  </button>
                )}
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: t('profile.accountType'), value: t('profile.standard') },
                { label: t('profile.plan'), value: user?.plan === 'trial' ? t('subscription.freeTrial') : (user?.plan || '—') },
              ].map(r => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-subtle)' }}>{r.label}</span>
                  <span style={{ fontWeight: 500, color: 'var(--text)' }}>{r.value}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
